// TransferDetailsStep.tsx
import {
  useConsumerRelation,
  useUtilityRequestConfiguration,
} from "@features/cx/transfer/hooks";
import { DynamicForm } from "@shared/components/DynamicForm";
import { StepHelpers } from "@shared/components/Stepper";
import { toast } from "@shared/hooks/use-toast";
import { FormField, FormService } from "@shared/services/FormServices";
import { Button } from "@shared/ui/button";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { usePremise } from "@features/cx/consumerManagement/hooks";
import { consumerApi } from "@features/cx/consumerManagement/api";
import { SubAccountCard } from "./cards/SubAccountCard";
import { TenantAccountCard } from "./cards/TenantAccountCard";
import { TransferCard } from "./cards/TransferCard";
import { FIELD_CLASSES } from "@shared/utils/constants";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { ContextualHelp } from "@shared/ui/contextual-help";
import { FileText } from "lucide-react";
import { DocumentUploadForm, Document as UploadDocument } from "@features/cx/shared/components/DocumentUploadForm";
import { formatTerritoryAddress } from "@shared/utils/address";

interface TransferDetailsStepProps {
  remoteUtilityId: number;
  storageKey: string;
    logTimeUntilStep?: (step: number | string) => void;
  stepHelpers?: StepHelpers;
  currentStepIndex?: number;
  onNext?: () => void;
  onPrevious?: () => void;
  setValidationError?: (error: string) => void;
  clearValidationError?: () => void;
  isValidating?: boolean;
}

export function TransferDetailsStep({
  remoteUtilityId,
  storageKey,
  logTimeUntilStep,
  stepHelpers,
  currentStepIndex = 0,
  onNext,
  onPrevious,
  setValidationError,
  clearValidationError,
  isValidating,
}: TransferDetailsStepProps) {
  const dataLoadedRef = useRef(false);

  // **NEW: Email verification state**
  const [emailVerificationStatus, setEmailVerificationStatus] = useState<{
    newCustomer: 'unverified' | 'verifying' | 'verified' | 'failed';
  }>({
    newCustomer: 'unverified'
  });

  // Document upload state — exact same pattern as DocumentUploadStep
  const [uploadedDocuments, setUploadedDocuments] = useState<UploadDocument[]>([]);

  const handleDocumentSave = useCallback((uploadedDocs: UploadDocument[]) => {
    // Process documents (same as DocumentUploadStep)
    const processedDocuments: UploadDocument[] = uploadedDocs.map(doc => ({
      ...doc,
      needsReupload: !doc.file,
      status: doc.file ? ('completed' as const) : ('error' as const),
    }));

    setUploadedDocuments(processedDocuments);

    // Prepare for storage — File objects can't be serialised to JSON
    const documentsForStorage = processedDocuments.map(doc => ({
      ...doc,
      file: doc.file ? true : (false as any),
    }));

    // Extract actual File objects for parent/submission
    const validDocuments = processedDocuments.filter(doc => doc.file && !doc.needsReupload);
    const fileObjects = validDocuments.map(doc => doc.file).filter(Boolean) as File[];
    const fileMetadata = processedDocuments.map(doc => ({
      id: doc.id,
      name: doc.name,
      type: doc.type,
      file: doc.file,
      category: doc.category,
      subCategory: doc.subCategory,
      categoryCode: doc.categoryCode,
      subCategoryCode: doc.subCategoryCode,
      size: doc.size,
      needsReupload: doc.needsReupload || false,
      referenceId: doc.referenceId || null,
    }));

    const formData = {
      documents: documentsForStorage,
      fileObjects,
      fileMetadata,
      isStepComplete: validDocuments.length > 0,
      completedAt: new Date().toISOString(),
    };

    // Save to stepHelpers (merged so form fields are not overwritten)
    if (stepHelpers) {
      try {
        const existing = stepHelpers.getStepData(currentStepIndex) || {};
        stepHelpers.setStepData(currentStepIndex, { ...existing, ...formData });
      } catch (_) {}
    }

    // Pass file data to parent via window — same as DocumentUploadStep
    // This populates create.tsx fileObjects/fileMetadata for ReviewSubmitStep
    if (typeof window !== 'undefined' && (window as any).handleFileDataChange) {
      (window as any).handleFileDataChange({
        documents: processedDocuments,
        file: fileObjects,
        fileMetadata,
        notes: '',
        incidentDate: '',
        expectedResolution: '',
      });
    }
  }, [stepHelpers, currentStepIndex]);

  const [accountTransferStatus, setAccountTransferStatus] = useState<'transferred_to_owner' | 'transferred_to_new_tenant' | "transferred_to_master" | "transferred_to_new_owner" | null>();
  
  const [selectedOption, setSelectedOption] = useState<"Master" | "New" | "Owner" | null>(null);

  // Get transfer type configurations from API
  const { data: transferConfigurations, isLoading: isLoadingTransferTypes } =
    useUtilityRequestConfiguration({
      remoteUtilityId,
      requestType: "Transfer",
      disablePagination: true,
    });

  // Transform transfer configurations to form options
  const transferTypeOptions =
    transferConfigurations?.result?.map((config) => ({
      value: config.id,
      label: config.name,
      version: config.version,
    })) || [];

  const { data: relationData } = useConsumerRelation();
  const relationOptions =
    relationData?.result?.map((config) => ({
      value: config.key,
      label: config.value,
    })) || [];

  console.log("transferConfigurations", transferConfigurations);

  // Fetch premises using hooks (for dynamic premise dropdown)
  const { data: premisesData } = usePremise({
    remote_utility_id: remoteUtilityId,
    config_level: "premise",
  });

  // Transform premises to options
  const premiseOptions = (premisesData?.result || []).map((premise: any) => ({
    value: premise.code,
    label: premise.name,
    data: premise,
  }));

  // **NEW: Email verification function (copied from ConsumerDetailsStep)**
  const verifyEmailForSubmission = useCallback(async (email: string): Promise<boolean> => {
    
    if (!email || email.length < 5 || !email.includes('@')) {
      return false;
    }

    try {
      // Set verification status to verifying
      setEmailVerificationStatus(prev => ({
        ...prev,
        newCustomer: 'verifying'
      }));

      // Direct API call (same as ConsumerDetailsStep)
      const response = await consumerApi.getEmailVerify(email, Number(remoteUtilityId));
      
      // Check if verification was successful
      const isVerified = response && (
        response.success === true ||
        response.verified === true ||
        response.status === "success" ||
        (response.message && response.message.toLowerCase().includes('success')) ||
        (response.message && response.message.toLowerCase().includes('verified'))
      );
      
      if (isVerified) {
        // Success
        setEmailVerificationStatus(prev => ({
          ...prev,
          newCustomer: 'verified'
        }));
        
        // Update form data with verification status
        const formData = transferForm.getValues();
        const updatedData = {
          ...formData,
          newCustomerEmailVerified: true
        };
        
        // Save updated data
        saveStepData(updatedData);
        
        console.log(`✅ New customer email verified successfully`);
        return true;
      } else {
        // Failed verification
        setEmailVerificationStatus(prev => ({
          ...prev,
          newCustomer: 'failed'
        }));
        
        console.log(`❌ New customer email verification failed`);
        return false;
      }
      
    } catch (error: any) {
      console.error(`❌ New customer email verification error:`, error);
      
      // Set verification status to failed
      setEmailVerificationStatus(prev => ({
        ...prev,
        newCustomer: 'failed'
      }));
      
      return false;
    }
  }, [remoteUtilityId]);

  // Helper function to save step data using stepHelpers
  const saveStepData = useCallback(
    (data: any) => {
      if (!stepHelpers) return;

      try {
        const stepData = {
          ...data,
          timestamp: new Date().toISOString(),
          stepIndex: currentStepIndex,
        };

        stepHelpers.setStepData(currentStepIndex, stepData);
        console.log("Step data saved via stepHelpers:", stepData);
      } catch (error) {
        console.error("Failed to save step data via stepHelpers:", error);
        toast({
          title: "Failed to save progress",
          description: "Your progress may not be saved. Please try again.",
          variant: "destructive",
        });
      }
    },
    [stepHelpers, currentStepIndex]
  );

  // Helper function to get step data using stepHelpers
  const getStepData = useCallback(() => {
    if (!stepHelpers) return null;

    try {
      return stepHelpers.getStepData(currentStepIndex);
    } catch (error) {
      console.error("Failed to get step data via stepHelpers:", error);
      return null;
    }
  }, [stepHelpers, currentStepIndex]);

  const getPreviousStepData = useCallback(() => {
    if (!stepHelpers) return null;

    try {
      return stepHelpers.getStepData(currentStepIndex-1);
    } catch (error) {
      console.error("Failed to get step data via stepHelpers:", error);
      return null;
    }
  }, [stepHelpers, currentStepIndex]);

  const accountType= useMemo(() => {
    const prevData = getPreviousStepData();
    return prevData?.ownershipDisplay;
  }, []);


  // Helper to get premise by code
  const getPremiseByCode = (premiseCode) => {
    return premiseOptions.find((p) => p.value === premiseCode)?.data || null;
  };

  // Create form fields
  const transferFields: FormField[] = useMemo(() => [
    {
      name:'card',
      label:'',
      type:'custom',
      customRender: () => {
        const prevData = getPreviousStepData();
        console.log("prevData", prevData);
        // const accountInfo = {
        //   name: prevData?.customerName || "",
        //   number: prevData?.accountNumber || "",
        //   phone: prevData?.contactNumber || "",
        //   address: prevData?.premiseAddress || ""
        // };
        const approvalDetails=prevData?.ownershipDetails?.approvalDetails;

        const accountInfo = {
          name: approvalDetails?.masterAccountName || approvalDetails?.ownerName|| "NA",
          number: approvalDetails?.masterAccountNo || approvalDetails?.ownerNo|| "NA",
          phone: approvalDetails?.masterPhone || approvalDetails?.ownerPhone|| "NA",
          address: formatTerritoryAddress({service:approvalDetails?.masterAddress},"service") || approvalDetails?.ownerAddress|| "NA"
        };
        
        if (accountType === "Sub-Account" && prevData?.showAlert === true) {
          return <SubAccountCard showAlert={true} masterAccount={accountInfo} />;
        }

        if (accountType === "Tenant" && prevData?.showAlert === true) {
          return <TenantAccountCard showAlert={true} owner={accountInfo} />;
        }

        return <></>;
      },
      classes: {
        container: "w-full mb-6 col-span-2",
      }
    },
    {
      name: "transferType",
      label: "Transfer Type",
      type: "select",
      required: true,
      options: transferTypeOptions,
      helperText:
        "Select the appropriate transfer type based on your situation",
      showHelperTooltip: true,
      tooltipText:
        "Full ownership transfers all account responsibilities, business transfers are for commercial entities, and deceased transfers handle estate situations.",
      fullWidth: true,
      classes: FIELD_CLASSES,
    },
    {
      name: "effectiveDate",
      label: "Effective Date",
      type: "datepicker",
      required: true,
      helperText: "The date when the transfer will take effect",
      showHelperTooltip: true,
      tooltipText:
        "The date when the transfer will take effect. This cannot be in the past. Allow 3-5 business days for processing.",
     
      fullWidth: true,
      classes: FIELD_CLASSES,
      
    },
    {
      name:'Account Selection',
      label:'',
      type:'custom',
      customRender: ({ field }: any) => {
        console.log('in custom render card',accountType)
        const showAlert = prevData?.showAlert;
        const approvalDetails=prevData?.ownershipDetails?.approvalDetails;
        const accountInfo = {
          name: approvalDetails?.masterAccountName || approvalDetails?.ownerName|| "NA",
          number: approvalDetails?.masterAccountNo || approvalDetails?.ownerNo|| "NA",
          phone: approvalDetails?.masterAccountEmail || approvalDetails?.ownerPhone|| "NA",
          address: approvalDetails?.masterAccountAddress || approvalDetails?.ownerAddress|| "NA"
        };
        
        const onOptionChange = (val: any) => {
          setSelectedOption(val);
          transferForm.setValue("Account Selection", val, { shouldValidate: true });
          // Immediately persist the 
          //  selection so it survives back-navigation
          if (stepHelpers) {
            try {
              const existing = stepHelpers.getStepData(currentStepIndex) || {};
              stepHelpers.setStepData(currentStepIndex, {
                ...existing,
                selectedOption: val,
                "Account Selection": val,
              });
            } catch (_) {}
          }
        };

        if (accountType === "Sub-Account" && !showAlert) {
          return (
            <SubAccountCard
              showAlert={showAlert}
              selectedOption={selectedOption}
              onOptionChange={onOptionChange}
              masterAccount={accountInfo}
            />
          );
        }

        if (accountType === "Tenant" && !showAlert) {
          return (
            <TenantAccountCard
              showAlert={showAlert}
              selectedOption={selectedOption}
              onOptionChange={onOptionChange}
              owner={accountInfo}
            />
          );
        }

        return <></>;
      },
      classes: {
        container: "w-full mb-6 col-span-2",
        error: "text-red-600 text-sm mt-1",
      },
      required: true,
      
    },
     {
      name:'new-customer-card',
      label:'',
      type:'custom',
      customRender: () => {
        const ownershipDetails=prevData?.ownershipDetails?.approvalDetails;
        return <TransferCard 
        title={selectedOption === "Master" ? "Transfer will be made to Master Account" : "Transfer will be made to Owner"}
        name={ownershipDetails?.masterAccountName ?? ownershipDetails?.ownerName ??  'NA'}
        phone={ownershipDetails?.masterAccountEmail ?? ownershipDetails?.ownerPhone ?? 'NA'}
        accountNumber={ownershipDetails?.masterAccountNo ?? ownershipDetails?.ownerNo ?? 'NA'}
        address={ownershipDetails?.masterAccountAddress ?? ownershipDetails?.ownerAddress ?? 'NA'}
      />;
      },
      showWhen(formValues, fieldPath) {
        return selectedOption === "Master" || selectedOption === "Owner";
      },
      classes: {
        container: "w-full mb-6 col-span-2",
      }
    },
    {
      name: "newCustomerFirstName",
      label: "New Customer First Name",
      type: "text",
      required: true,
      placeholder: "Enter first name",
      helperText: "Legal first name as it appears on identification",
      showHelperTooltip: true,
      tooltipText:
        "Enter the legal first name as it appears on government-issued identification.",
      fullWidth: true,
      showWhen(formValues) {
        return formValues["Account Selection"] === "New";
      },
      
      classes: {
        container: "w-full mb-6",
        label: "block text-sm font-medium text-gray-700 mb-2",
        input: "w-full h-12 px-4 border border-gray-300 rounded-lg",
        error: "text-red-600 text-sm mt-1",
        helperText: "text-xs text-gray-500 mt-1",
      },
    },
    {
      name: "newCustomerLastName",
      label: "New Customer Last Name",
      type: "text",
      required: true,
      placeholder: "Enter last name",
      helperText: "Legal last name as it appears on identification",
      showHelperTooltip: true,
      tooltipText:
        "Enter the legal last name as it appears on government-issued identification.",
      fullWidth: true,
       showWhen(formValues) {
        return formValues["Account Selection"] === "New";
      },
      
      classes: {
        container: "w-full mb-6",
        label: "block text-sm font-medium text-gray-700 mb-2",
        input: "w-full h-12 px-4 border border-gray-300 rounded-lg",
        error: "text-red-600 text-sm mt-1",
        helperText: "text-xs text-gray-500 mt-1",
      },
    },
    {
      name: "newCustomerEmail",
      label: "New Customer Email",
      type: "email",
      required: true,
      placeholder: "Enter email address",
      helperText: "Primary email for account communications",
      showHelperTooltip: true,
       showWhen(formValues) {
        return formValues["Account Selection"] === "New";
      },
      tooltipText:
        "This email will be used for all account communications, bills, and notifications.",
      // **NEW: Disable field when verified (same as ConsumerDetailsStep)**
      disabled: emailVerificationStatus.newCustomer === 'verified',
      
      fullWidth: true,
     
      classes: {
        container: "w-full mb-6",
        label: "block text-sm font-medium text-gray-700 mb-2",
        // **NEW: Added disabled styles**
        input: "w-full h-12 px-4 border border-gray-300 rounded-lg disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-500",
        error: "text-red-600 text-sm mt-1",
        helperText: "text-xs text-gray-500 mt-1",
      },
    },
    {
      name: "newCustomerPhone",
      label: "New Customer Phone",
      type: "phone",
      required: true,
      placeholder: "(000) 000-0000",
      helperText: "Primary contact phone number",
      showHelperTooltip: true,
       showWhen(formValues) {
        return formValues["Account Selection"] === "New";
      },
      tooltipText:
        "Provide the best phone number to reach the new account holder.",
      fullWidth: true,
      
      classes: {
        container: "w-full mb-6",
        label: "block text-sm font-medium text-gray-700 mb-2",
        input: "w-full h-12 px-4 border border-gray-300 rounded-lg",
        error: "text-red-600 text-sm mt-1",
        helperText: "text-xs text-gray-500 mt-1",
      },
    },
    {
      name: "relationship",
      label: "Relationship to Current Customer",
      type: "select",
      required: true,
      options: relationOptions,
       showWhen(formValues) {
        return formValues["Account Selection"] === "New";
      },
      helperText: "Relationship between current and new account holder",
      showHelperTooltip: true,
      tooltipText:
        "Select the relationship between the current account holder and the new account holder.",
      fullWidth: true,
      classes: {
        container: "w-full mb-6 col-span-2",
        label: "block text-sm font-medium text-gray-700 mb-2",
        input: "w-full h-12 px-4 border border-gray-300 rounded-lg",
        error: "text-red-600 text-sm mt-1",
        select:
          "w-full h-12 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500",
        helperText: "text-xs text-gray-500 mt-1",
      },
    },
    {
      name: "documentUpload",
      label: "",
      type: "custom",
      showWhen(formValues) {
        return formValues["Account Selection"] === "New";
      },
      customRender: () => {
        // When we have restored documents (needsReupload=true from stepHelpers),
        // switch to edit mode so DocumentUploadForm actually reads them back.
        // In pure create mode it always ignores initialData and resets to placeholders.
        const hasRestoredDocs = uploadedDocuments.some(d => d.needsReupload && d.name);

        const consumerDetailsForEdit = hasRestoredDocs
          ? {
              document: uploadedDocuments
                .filter(d => d.needsReupload && d.name)
                .map(d => ({
                  documentType: d.category,      // category NAME — matched by DocumentUploadForm
                  documentSubtype: d.subCategory, // subCategory NAME
                  file: d.name,                  // filename used as display label
                  referenceId: d.referenceId,
                })),
            }
          : undefined;

        return (
          <Card className="border-l-4 border-l-purple-500 col-span-2">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center justify-between text-lg">
                <div className="flex items-center">
                  <FileText className="mr-2 h-5 w-5 text-purple-500" />
                  Required Documentation
                </div>
                <ContextualHelp
                  title="Document Requirements"
                  content="Upload all required documents to support your transfer request. The specific documents needed may vary based on the transfer type. Ensure all documents are clear, legible, and current. Accepted formats include PDF, JPG, PNG, and TIFF."
                />
              </CardTitle>
            </CardHeader>
            <CardContent>
              <DocumentUploadForm
                key={hasRestoredDocs ? "transfer-doc-restored" : "transfer-doc-fresh"}
                initialData={uploadedDocuments}
                onSave={handleDocumentSave}
                isEditMode={hasRestoredDocs}
                consumerDetails={consumerDetailsForEdit}
                showSubmitButton={false}
                remoteUtilityId={remoteUtilityId}
              />
            </CardContent>
          </Card>
        );
      },
      classes: {
        container: "w-full mb-6 col-span-2",
      },
    },
    {
      name: "changeBillingAddress",
      label: "Change Mailing Address?",
      type: "select",
      required: true,
      isSearchable:false,
      options: [
        { value: "yes", label: "Yes, update address" },
        { value: "no", label: "No, keep existing address" },
      ],
      helperText: "Choose whether to update the billing address",
      showHelperTooltip: true,
      tooltipText:
        "You can choose to keep the existing billing address or update it.",
      fullWidth: true,
      showWhen: (formValues: any) => selectedOption === "New",
      classes: {
        container: "w-full mb-6 col-span-2",
        label: "block text-sm font-medium text-gray-700 mb-2",
        input:
          "w-full h-12 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500",
        error: "text-red-600 text-sm mt-1",
        select:
          "w-full h-12 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500",
        helperText: "text-xs text-gray-500 mt-1",
      },
    },
    {
      name: "unit",
      label: "House/Unit Number",
      type: "text",
      required: true,
      placeholder: "e.g 123,Apt 4B",
      helperText: "Enter your building or unit identifier",
      showWhen: (formValues: any) => formValues.changeBillingAddress === "yes",
      
      classes: {
        container: "w-full mb-6 col-span-1",
        label: "block text-sm font-medium text-gray-700 mb-2",
        input: "w-full h-12 px-4 border border-gray-300 rounded-lg",
        error: "text-red-600 text-sm mt-1",
        helperText: "text-xs text-gray-500 mt-1",
      },
    },
    {
      name: "streetAddress",
      label: "Street Address",
      type: "text",
      required: true,
      placeholder: "e.g 123 Main Street",
      helperText: "Enter the full street address without apartment/unit number",
      showWhen: (formValues: any) => formValues.changeBillingAddress === "yes" ,
     
      classes: {
        container: "w-full mb-6 col-span-1",
        label: "block text-sm font-medium text-gray-700 mb-2",
        input: "w-full h-12 px-4 border border-gray-300 rounded-lg",
        error: "text-red-600 text-sm mt-1",
        helperText: "text-xs text-gray-500 mt-1",
      },
    },
    {
      name: "premiseType",
      label: "Premise",
      type: "select",
      required: true,
      searchable: true,
      placeholder: "Select premise",
      options: premiseOptions,
      helperText: "Select the premise for the new billing address",
      showWhen: (formValues: any) => formValues.changeBillingAddress === "yes",
    
      classes: {
        container: "w-full mb-6 col-span-1",
        label: "block text-sm font-medium text-gray-700 mb-2",
        select:
          "w-full h-12 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500",
        error: "text-red-600 text-sm mt-1",
        helperText: "text-xs text-gray-500 mt-1",
      },
    },
    {
      name: "zipCode",
      label: "Zip/Postal Code",
      type: "text",
      required: true,
      placeholder: "e.g. 12345",
      helperText: "Enter your postal code",
      showWhen: (formValues: any) => formValues.changeBillingAddress === "yes",
      
      classes: {
        container: "w-full mb-6 col-span-1",
        label: "block text-sm font-medium text-gray-700 mb-2",
        input: "w-full h-12 px-4 border border-gray-300 rounded-lg",
        error: "text-red-600 text-sm mt-1",
        helperText: "text-xs text-gray-500 mt-1",
      },
    },
    {
      name: "financialResponsibility",
      label: "Financial Responsibility",
      type: "select",
      required: true,
      isSearchable: false,
      options: [
        {
          value: "transfer",
          label:
            "Transfer outstanding balance - Outstanding balance will be transferred to the new account holder",
        },
        {
          value: "clear",
          label:
            "Clear balance before transfer - Current account holder must pay outstanding balance first",
        },
      ],
      helperText: "Choose how to handle outstanding balance",
      showHelperTooltip: true,
      tooltipText:
        "Transfer: New account holder assumes responsibility for existing balance. Clear: Current balance must be paid before transfer.",
      fullWidth: true,
     
      classes: {
        container: "w-full mb-6 col-span-2",
        label: "block text-sm font-medium text-gray-700 mb-2",
        input:
          "w-full h-12 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500",
        error: "text-red-600 text-sm mt-1",
        select:
          "w-full h-12 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500",
        helperText: "text-xs text-gray-500 mt-1",
      },
    },
    {
      name: "specialInstructions",
      label: "Special Instructions",
      type: "textarea",
      placeholder:
        "Add any special instructions, notes, or requirements for this transfer",
      helperText: "Additional information for processing",
      showHelperTooltip: true,
      tooltipText:
        "Include any special circumstances, specific timing requirements, or additional information that may be relevant to processing this transfer request.",
      fullWidth: true,
      showWhen(formValues) {
        return formValues["Account Selection"] === "New";
      },
      customValidation: (value: any) => {
        if (value && value.length > 500) return "Instructions are too long (max 500 characters)";
        return true;
      },
      classes: {
        container: "w-full mb-4 col-span-2",
        label: "block text-sm font-medium text-gray-700 mb-1",
        textarea:
          "w-full h-24 px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors bg-white resize-none",
        error: "text-red-600 text-sm mt-1",
        helperText: "text-gray-500 text-xs mt-1",
      },
    },
  ], [
    getPreviousStepData,
    accountType,
    transferTypeOptions,
    setSelectedOption,
    selectedOption,
    stepHelpers,
    currentStepIndex,
    emailVerificationStatus,
    uploadedDocuments,
    handleDocumentSave,
    remoteUtilityId,
    relationOptions,
    premiseOptions,
  ]);

  // Create Form Instance
  const transferForm = FormService.createForm({
    fields: transferFields,
    mode: "onChange",
    shouldUnregister: false,
    validateOnMount: false,
    defaultValues: {
      transferType:
        transferTypeOptions.length > 0 ? transferTypeOptions[0].value : "",
      effectiveDate: "",
      newCustomerFirstName: "",
      newCustomerLastName: "",
      newCustomerEmail: "",
      newCustomerPhone: "",
      relationship: "",
      changeBillingAddress: "no",
      billingAddress: "",
      financialResponsibility: "transfer",
      specialInstructions: "",
      // **NEW: Add email verification field**
      newCustomerEmailVerified: false,
      "Account Selection": "",
    },
  });

 const prevData = getPreviousStepData();
  // Set account type defaults on first mount — but restore saved values if data exists
  useEffect(() => {
    const stepData = getStepData();
   
    console.log('previous data-----',prevData,accountType)
    
    // Only reset to defaults if there is no saved session for this step
    if (prevData) {
        if(accountType == "Master" || accountType == "Individual" || (accountType === "Sub-Account" &&
    prevData?.showAlert === true ) ||  (accountType === "Tenant" &&
    prevData?.showAlert === true )){
          setSelectedOption("New");
        transferForm.setValue("Account Selection", 'New');
      }
    }
    if (stepData?.newCustomerEmailVerified) {
      setEmailVerificationStatus(prev => ({ ...prev, newCustomer: 'verified' }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountType, prevData?.showAlert]);

  // Load saved data from stepHelpers on component mount
  useEffect(() => {
    if (stepHelpers && !dataLoadedRef.current && !isLoadingTransferTypes && relationData) {
      try {
        const savedStepData = getStepData();
        if (savedStepData) {
          console.log("Loaded step data from stepHelpers:", savedStepData);

          // Restore form fields
          FormService.populateForm(transferForm, savedStepData, transferFields);

          // Deferred re-populate: conditional fields (e.g. 'relationship') only render
          // after Account Selection is set to "NEW". With shouldUnregister:false this is safe,
          // but we still re-set + trigger so RHF validation state is in sync with the values.
          setTimeout(() => {
            FormService.populateForm(transferForm, savedStepData, transferFields);
            // transferForm.trigger();
          }, 100);

          // Restore radio button state (selectedOption + accountType)
          if (savedStepData.selectedOption) {
            setSelectedOption(savedStepData.selectedOption as "Master" | "New" | "Owner");
          }
          
          // Restore documents — exact same logic as DocumentUploadStep
          if (savedStepData.documents && Array.isArray(savedStepData.documents)) {
            const restoredDocuments: UploadDocument[] = savedStepData.documents.map((doc: any) => {
              // Detect if there was a file — stored as:
              //   true  → properly serialized by handleDocumentSave
              //   {}    → File object that JSON.stringify turned into empty object (via handleFormSubmit)
              //   false → placeholder, no file
              const hadFile =
                doc.file === true ||
                (doc.file !== null &&
                  doc.file !== false &&
                  doc.file !== undefined &&
                  typeof doc.file === 'object' &&
                  !!doc.name); // only count as "had file" if there was an actual filename

              return {
                ...doc,
                file: hadFile ? true : undefined,
                needsReupload: hadFile,
                status: hadFile ? ('completed' as const) : ('uploading' as const),
              };
            });

            setUploadedDocuments(restoredDocuments);

            // Show toast for files that need re-uploading (same as DocumentUploadStep)
            const filesNeedingReupload = restoredDocuments.filter(doc => doc.needsReupload);
            if (filesNeedingReupload.length > 0) {
              toast({
                title: "Previous documents restored",
                description: `${filesNeedingReupload.length} document(s) from previous session need to be re-uploaded.`,
                variant: "default",
              });
            }

            // Re-populate parent file state (same as DocumentUploadStep)
            if (typeof window !== 'undefined' && (window as any).handleFileDataChange) {
              (window as any).handleFileDataChange({
                documents: restoredDocuments,
                file: [],   // No actual File objects after reload
                fileMetadata: restoredDocuments.map((doc: any) => ({
                  id: doc.id,
                  name: doc.name,
                  type: doc.type,
                  category: doc.category,
                  subCategory: doc.subCategory,
                  categoryCode: doc.categoryCode,
                  subCategoryCode: doc.subCategoryCode,
                  size: doc.size,
                  needsReupload: doc.needsReupload || false,
                  referenceId: doc.referenceId || null,
                })),
                notes: '',
                incidentDate: '',
                expectedResolution: '',
              });
            }
          }
        }
        dataLoadedRef.current = true;
      } catch (error) {
        console.error("Failed to load step data from stepHelpers:", error);
        dataLoadedRef.current = true;
      }
    }
  }, [
    stepHelpers,
    currentStepIndex,
    transferForm,
    getStepData,
    isLoadingTransferTypes,
    relationData,
  ]);

  // Show error state if no transfer types are available
  if (!isLoadingTransferTypes && transferTypeOptions.length === 0) {
    return (
      <div className="space-y-6">
        <div className="text-center py-8">
          <p className="text-red-600 mb-4">
            No transfer types are currently available.
          </p>
          <p className="text-gray-600">
            Please contact support or try again later.
          </p>
        </div>
      </div>
    );
  }

  // **UPDATED: Enhanced handleFormSubmit with email verification (same logic as ConsumerDetailsStep)**
  const handleFormSubmit = async (data: any) => {
    // Clear any previous validation errors
    if (clearValidationError) {
      clearValidationError();
    }

   

    // Validate using FormService (respects dynamic visibility)
    const isValid = await FormService.validateForm(transferForm);

    if (!isValid) {
      const errorMessage = "Please fill in all required fields.";
      toast({
        title: errorMessage,
        variant: "destructive",
      });

      if (setValidationError) {
        setValidationError(errorMessage);
      }
      return;
    }

    console.log('selected option is:',selectedOption)

    if(selectedOption === null){
      toast({
        title: "Please select how would you like to proceed with this transfer",
        variant: "destructive",
      });
      return;
    }

    // **NEW: Email verification before submission (same as ConsumerDetailsStep)**
    try {
      // Verify new customer email if not already verified
      if (data.newCustomerEmail && emailVerificationStatus.newCustomer !== 'verified' && selectedOption === "New") {
        console.log('🔍 Verifying new customer email before submission:', data.newCustomerEmail);
        const emailVerified = await verifyEmailForSubmission(data.newCustomerEmail);
        if (!emailVerified) {
          const message = 'New customer email verification failed. Please check the email address and try again.';
          toast({
            title: 'Email Verification Failed',
            description: message,
            variant: 'destructive',
          });
          if (setValidationError) {
            setValidationError(message);
          }
          return;
        }
      }
    } catch (error) {
      console.error('❌ Email verification failed during submission:', error);

      const errorMessage =
        (error as any)?.response?.data?.message?.[0] ||
        (error as any)?.message ||
        'Email verification failed. Please try again.';

      toast({
        title: 'Email Verification Failed',
        description: errorMessage,
        variant: 'destructive',
      });

      if (setValidationError) {
        setValidationError(errorMessage);
      }
      return;
    }

    // FIX: Convert data.transferType to number for comparison and add debugging
    console.log(
      "data.transferType:",
      data.transferType,
      "type:",
      typeof data.transferType
    );
    console.log("transferConfigurations:", transferConfigurations?.result);

    const selectedTransfer = transferConfigurations?.result?.find((option) => {
      // Convert both to numbers for comparison
      const optionId = Number(option.id);
      const transferTypeId = Number(data.transferType);
      console.log("Comparing:", optionId, "with", transferTypeId);
      return optionId === transferTypeId;
    });

    console.log("selectedTransfer:", selectedTransfer);
    const transferTypeVersion = selectedTransfer?.version || "";
    console.log("transferTypeVersion:", transferTypeVersion);

    // Find the selected premise object
    const selectedPremise = premiseOptions.find(
      (p) => p.value === data.premiseType
    )?.data;
    const hierarchyCodes = selectedPremise?.hierarchyCodes || {};
    const hierarchyNames = selectedPremise?.hierarchyNames || {};
    console.log("Codesssss", hierarchyCodes);
    console.log("Namessss", hierarchyNames);

    console.log("data.relationship:", data.relationship);
    console.log("relationOptions:", relationOptions);
    // Transform data to match original structure
    const formData = {
      transferType: data.transferType,
      transferTypeLabel:
        transferTypeOptions.find(
          (option) => Number(option.value) === Number(data.transferType)
        )?.label || "",

      newCustomer: {
        firstName: data.newCustomerFirstName,
        lastName: data.newCustomerLastName,
        email: data.newCustomerEmail,
        phoneNumber: data.newCustomerPhone,
        relationship: data.relationship,
        relationshipLabel:
          relationOptions.find(
            (option) => Number(option.value) === Number(data.relationship)
          )?.label || "",
        // **NEW: Include email verification status**
        emailVerified: data.newCustomerEmailVerified || emailVerificationStatus.newCustomer === 'verified',
      },
      changeBillingAddress: data.changeBillingAddress,
      billingAddress: data.billingAddress,
      effectiveDate: data.effectiveDate,
      financialResponsibility: data.financialResponsibility,
      specialInstructions: data.specialInstructions,
      // Radio / card selection state
      selectedOption,
      accountType,
      documents: uploadedDocuments.map(doc => ({
        ...doc,
        // Serialize File objects as boolean — File→JSON becomes {}, both are lost on reload
        file: (doc.file && typeof doc.file !== 'boolean') ? true : (doc.file || false) as any,
      })),
      isStepComplete: true,
      completedAt: new Date().toISOString(),
      version: transferTypeVersion,
      premiseType: data.premiseType,
      premiseHierarchyCodes: hierarchyCodes,
      premiseHierarchyNames: hierarchyNames,
      // **NEW: Include email verification in step data**
      newCustomerEmailVerified: data.newCustomerEmailVerified || emailVerificationStatus.newCustomer === 'verified',
    };

    // Save to stepHelpers
    saveStepData(formData);
    logTimeUntilStep('Transfer Details');
    if (onNext) {
      onNext();
    }
  };

  const handleFieldChange = (fieldName: string, value: any) => {
    // Get current step data and update the specific field
    const currentData = getStepData() || {};
    const updatedData = {
      ...currentData,
      [fieldName]: value,
      lastUpdated: new Date().toISOString(),
    };

    // Auto-populate area, subarea, city when premiseType changes
    if (fieldName === "premiseType" && value) {
      const premise = getPremiseByCode(value);
      console.log("premise dataaaaaaaaaaaa", premise.hierarchyCodes);
      if (premise && premise.hierarchyNames) {
        console.log("Selected premise hierarchyNames:", premise.hierarchyNames);
        transferForm.setValue("city", premise.hierarchyNames.state || "");
        transferForm.setValue("area", premise.hierarchyNames.area || "");
        transferForm.setValue(
          "subarea",
          premise.hierarchyNames.subArea || premise.hierarchyNames.subarea || ""
        );
      }
    }

    // Save updated data using stepHelpers
    saveStepData(updatedData);
  };

  return (
    <div className="space-y-6">
      
      <div>
        <p className="text-gray-600">
          Complete the transfer details form below. All required fields must be
          filled to proceed. Email verification is required before submission.
        </p>
      </div>

      <DynamicForm
        fields={transferFields}
        form={transferForm}
        onSubmit={handleFormSubmit}
        onFieldChange={handleFieldChange}
        config={{
          showSubmitButton: false,
          showResetButton: false,
        }}
        gridColumns={2}
        responsiveColumns={{ sm: 1, md: 2 }}
      />
      <div className="flex justify-between pt-4">
        <Button variant="outline" onClick={onPrevious}>
          Previous
        </Button>
        <Button
          onClick={() => handleFormSubmit(transferForm.getValues())}
          disabled={
            isValidating ||
            isLoadingTransferTypes ||
            transferTypeOptions.length === 0
          }
          className="min-w-[200px]"
        >
          {isValidating ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Validating...
            </>
          ) : isLoadingTransferTypes ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Loading...
            </>
          ) : emailVerificationStatus.newCustomer === 'verifying' ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Verifying Email...
            </>
          ) : (
            "Continue to Documentation"
          )}
        </Button>
      </div>
    </div>
  );
}